import React, { useState } from 'react';
import Section from './Section';
import SmartBomModal, { SmartBomSheetTab } from '../../components/bom/smartbom/SmartBomModal';
import SmartBomButton from '../../components/bom/smartbom/SmartBomButton';
import SmartBomRevealCard from '../../components/bom/smartbom/SmartBomRevealCard';
import { SmartBomCheckpointDialog } from '../../components/bom/smartbom/SmartBomCheckpoint';
import CriteriaBrief from '../../components/criteria/CriteriaBrief';
import { SAMPLE_CRITERIA_CHIPS, SAMPLE_NOTE_CHIPS } from './criteriaBriefFixtures';
import {
  BomVersionDetail,
  BomVersionMeta,
  BomVersionComponent,
  BomReasoningRow,
} from '../../types/bomVersions';

/**
 * /design — Smart BOM states on the supplier-discovery sub-step. Static
 * sample data shaped exactly like the `/trp/bom/:bomId/versions*` responses.
 * Eyeball at `/design?only=smartbom`: the reveal card (fresh + dismissed
 * states), the quiet pill, version picker, editable latest sheet, read-only
 * older version note, Reasoning sheet, bottom sheet-tab strip.
 */

const COMPONENTS_V1: BomVersionComponent[] = [
  { componentId: 'COMP-1', name: 'Hub Motor 48V', partNumber: 'AMR-HM-48', description: 'Brushless hub motor for AMR drive', specifications: '48V, 500W, 150rpm', quantity: 2, manufacturer: 'Generic / Standard', targetPrice: 120, supplierCandidates: [] },
  { componentId: 'COMP-2', name: 'Motor Driver', mpn: 'VESC-6-MKVI', description: 'FOC motor controller', specifications: '48V, 40A peak', quantity: 2, manufacturer: 'Generic / Standard', targetPrice: 45, supplierCandidates: [] },
  { componentId: 'COMP-3', name: 'LiFePO4 Battery Pack', description: 'Traction battery', specifications: '48V, 30Ah', quantity: 1, manufacturer: 'Generic / Standard', targetPrice: 380, supplierCandidates: [] },
];

const COMPONENTS_V4: BomVersionComponent[] = [
  { ...COMPONENTS_V1[0], quantity: 4, targetPrice: 110, supplierCandidates: [
    { supplierName: 'Acme Drives', status: 'SHORTLISTED', hasContactEmail: true },
    { supplierName: 'Shenzhen MotorWorks', status: 'ANALYZED', hasContactEmail: false },
  ] },
  { componentId: 'COMP-2', name: 'Motor Driver 60A', description: 'FOC motor controller', specifications: '48V, 60A peak', quantity: 2, manufacturer: 'Generic / Standard', targetPrice: 52, supplierCandidates: [
    { supplierName: 'DriveTech GmbH', status: 'SHORTLISTED', hasContactEmail: true },
  ] },
  COMPONENTS_V1[2],
];

const VERSIONS: BomVersionMeta[] = [
  { version: 1, trigger: 'EXTRACTED', summary: 'Extracted 3 component(s) from 1 uploaded document(s).', changeCount: 0, componentCount: 3, createdBy: 'user-1', createdAt: '2026-09-02T09:14:00Z' },
  { version: 2, trigger: 'CONTEXT_APPLIED', summary: 'Applied 2 context answer(s) to the BOM analysis.', changeCount: 2, componentCount: 3, createdBy: 'user-1', createdAt: '2026-09-02T09:31:00Z' },
  { version: 3, trigger: 'COMPONENT_REPLACED', summary: 'Replaced "Motor Driver" with "Motor Driver 60A".', changeCount: 1, componentCount: 3, createdBy: 'user-1', createdAt: '2026-09-03T11:05:00Z' },
  { version: 4, trigger: 'MANUAL_EDIT', summary: 'Buyer edited 2 field(s) across 1 component(s).', changeCount: 2, componentCount: 3, createdBy: 'user-1', createdAt: '2026-09-04T16:42:00Z' },
];

const REASONING: BomReasoningRow[] = [
  { version: 1, date: '2026-09-02T09:14:00Z', trigger: 'EXTRACTED', componentName: '—', change: '—', why: 'Extracted 3 component(s) from 1 uploaded document(s).' },
  { version: 2, date: '2026-09-02T09:31:00Z', trigger: 'CONTEXT_APPLIED', componentName: 'Hub Motor 48V', change: 'dutyCycle → 16', why: 'Context answer recorded — feeds validation and supplier research' },
  { version: 2, date: '2026-09-02T09:31:00Z', trigger: 'CONTEXT_APPLIED', componentName: '—', change: 'operatingVoltage → 48', why: 'Context answer recorded — feeds validation and supplier research' },
  { version: 3, date: '2026-09-03T11:05:00Z', trigger: 'COMPONENT_REPLACED', componentName: 'Motor Driver 60A', change: 'component: Motor Driver → Motor Driver 60A', why: 'Rated for the regen spikes the BMS allows under full-load braking' },
  { version: 4, date: '2026-09-04T16:42:00Z', trigger: 'MANUAL_EDIT', componentName: 'Hub Motor 48V', change: 'quantity: 2 → 4', why: 'Edited by the buyer in the Smart BOM sheet' },
  { version: 4, date: '2026-09-04T16:42:00Z', trigger: 'MANUAL_EDIT', componentName: 'Hub Motor 48V', change: 'targetPrice: 120 → 110', why: 'Edited by the buyer in the Smart BOM sheet' },
];

const detailFor = (version: number): BomVersionDetail => {
  const meta = VERSIONS.find((v) => v.version === version) || VERSIONS[VERSIONS.length - 1];
  return {
    ...meta,
    bomId: 'BOM-GALLERY-1',
    changes: [],
    components: version >= 3 ? COMPONENTS_V4 : COMPONENTS_V1,
    reasoning: REASONING.filter((r) => r.version <= version),
  };
};

// Sum of changeCounts beyond the v1 baseline — the reveal card's honest
// number, computed from the sample list the same way the live hook does.
const CHANGES_BEYOND_V1 = VERSIONS.filter((v) => v.version > 1).reduce(
  (sum, v) => sum + v.changeCount,
  0,
);

const briefFor = (variant: 'full' | 'compact', surface: 'card' | 'strip' | 'none', labelled = true) => (
  <CriteriaBrief
    chips={SAMPLE_CRITERIA_CHIPS}
    noteChips={SAMPLE_NOTE_CHIPS}
    variant={variant}
    surface={surface}
    labelled={labelled}
  />
);

const SmartBomGallery: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [initialTab, setInitialTab] = useState<SmartBomSheetTab>('smart-bom');
  const [selected, setSelected] = useState<number>(4);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [revealDismissed, setRevealDismissed] = useState(false);
  const [checkpointOpen, setCheckpointOpen] = useState(false);
  const [checkpointReviewed, setCheckpointReviewed] = useState(false);

  const openOn = (tab: SmartBomSheetTab) => {
    setInitialTab(tab);
    setOpen(true);
  };

  return (
    <Section title="Smart BOM (discovery sub-step: reveal + pill + modal)">
      <div className="flex flex-col gap-s5">
        <p className="m-0 text-body-sm text-ink-secondary">
          The Smart BOM surfaces ONLY on supplier discovery. On arrival — when the version list
          has ≥2 versions and real changes beyond v1 — the reveal card animates in once per
          version state (remembered per BOM in localStorage; “Not now” and “See the smarter BOM”
          both count as seen, a newer version re-arms it). “See the smarter BOM” opens the modal
          on the Reasoning sheet; the quiet pill stays as the standing reference and opens the grid.
        </p>

        {/* Reveal card — fresh state, numbers derived from the sample versions. */}
        {revealDismissed ? (
          <div className="flex items-center gap-s4 rounded-card border border-dashed border-line px-s6 py-s4">
            <span className="text-meta text-ink-muted">
              Dismissed state: the card is simply gone — the pill remains. It returns only when a
              newer version exists than the one remembered.
            </span>
            <button
              type="button"
              onClick={() => setRevealDismissed(false)}
              className="ml-auto text-meta font-medium text-action hover:underline"
            >
              Reset reveal
            </button>
          </div>
        ) : (
          <SmartBomRevealCard
            latestVersion={VERSIONS[VERSIONS.length - 1].version}
            changeCount={CHANGES_BEYOND_V1}
            onSee={() => {
              setRevealDismissed(true);
              openOn('reasoning');
            }}
            onDismiss={() => setRevealDismissed(true)}
            criteria={briefFor('compact', 'none')}
          />
        )}

        <div className="flex items-center gap-s4">
          <SmartBomButton latestVersion={4} onClick={() => openOn('smart-bom')} />
          <span className="text-meta text-ink-muted">← the standing reference, grid first</span>
        </div>

        {/* Pre-discovery checkpoint — Continue-from-validation runs through
            this dialog before supplier research fires. */}
        <div className="flex items-center gap-s4">
          <button
            type="button"
            onClick={() => { setCheckpointReviewed(false); setCheckpointOpen(true); }}
            className="text-meta font-medium text-action hover:underline"
          >
            Open the pre-discovery checkpoint
          </button>
          <span className="text-meta text-ink-muted">
            ← shown when Continue on validation would start research and the BOM has unseen improvements
          </span>
        </div>
        <SmartBomCheckpointDialog
          open={checkpointOpen}
          latestVersion={VERSIONS[VERSIONS.length - 1].version}
          changeCount={CHANGES_BEYOND_V1}
          isFresh
          hasReviewed={checkpointReviewed}
          canEdit
          onReview={() => { setCheckpointReviewed(true); openOn('smart-bom'); }}
          onProceed={() => setCheckpointOpen(false)}
          onCancel={() => setCheckpointOpen(false)}
          criteria={briefFor('compact', 'card', false)}
        />

        <SmartBomModal
          open={open}
          onClose={() => setOpen(false)}
          initialTab={initialTab}
          versions={VERSIONS}
          selectedVersion={selected}
          detail={detailFor(selected)}
          loading={false}
          error={null}
          canEdit
          saving={false}
          downloading={false}
          savedNotice={savedNotice}
          onSelectVersion={(v) => {
            setSavedNotice(null);
            setSelected(v);
          }}
          onSave={() => setSavedNotice('Saved as v5. (gallery stub — nothing was written)')}
          onDownload={() => setSavedNotice('Download starts here in the app. (gallery stub)')}
          criteria={briefFor('full', 'strip')}
        />
      </div>
    </Section>
  );
};

export default SmartBomGallery;
